import { useEffect } from 'react';

import { Alert, Button, Stack, Typography } from '@cast/design-system';

import { raiseReportingMonetizationEvent } from './analytics/reportingMonetizationEvents';
import { isSoftOverage } from './reportingPlans';

type ReportingPlanHardUpgradeBannerProps = {
  planId: string;
  planName: string;
  planVcpuCap: number;
  avgProvisionedVcpu: number;
  onUpgrade?: () => void;
};

/**
 * Hard overage when paid usage exceeds plan by the soft threshold (10%+) —
 * messaging only (no console lock). Figma: “No new reporting data is being collected.”
 */
export const ReportingPlanHardUpgradeBanner = ({
  planId,
  planName,
  planVcpuCap,
  avgProvisionedVcpu,
  onUpgrade,
}: ReportingPlanHardUpgradeBannerProps) => {
  const show = isSoftOverage(avgProvisionedVcpu, planVcpuCap);

  useEffect(() => {
    if (!show) {
      return;
    }
    raiseReportingMonetizationEvent('reporting_paywall_viewed', {
      source: 'hard_upgrade_nudge',
      current_plan: planId,
      selected_bracket: String(planVcpuCap),
      gate_reason: 'over_plan_cap',
    });
  }, [planId, planVcpuCap, show]);

  if (!show) {
    return null;
  }

  return (
    <Alert color="error" size="sm" testId="reporting-plan-hard-upgrade-banner">
      <Stack gap={8} direction="row" alignItems="center" flexWrap="wrap">
        <Typography variant="P14R" flex={1}>
          <Typography variant="P14B" component="span">
            No new reporting data is being collected.{' '}
          </Typography>
          Your fleet averages ~{Math.round(avgProvisionedVcpu)} vCPUs, above the{' '}
          {planName} limit ({planVcpuCap}). Upgrade to restore collection.
        </Typography>
        {onUpgrade ? (
          <Button
            variant="danger"
            size="sm"
            onClick={onUpgrade}
            testId="reporting-plan-hard-upgrade-cta"
          >
            View upgrade options
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
};
